// filepath: client/src/components/SEO/BreadcrumbSEO.tsx
import React from 'react';
import { useLocation } from 'react-router-dom';
import { Breadcrumbs, Link, Typography, Box } from '@mui/material';
import { NavigateNext as NavigateNextIcon, Home as HomeIcon } from '@mui/icons-material';

interface BreadcrumbItem {
  label: string;
  href?: string;
  icon?: React.ReactNode;
}

interface BreadcrumbSEOProps {
  customBreadcrumbs?: BreadcrumbItem[];
  showHome?: boolean;
}

// Route-based breadcrumb configuration
const BREADCRUMB_CONFIG: Record<string, BreadcrumbItem[]> = {
  '/': [
    { label: 'Home', href: '/', icon: <HomeIcon sx={{ fontSize: 16 }} /> }
  ],
  '/support': [
    { label: 'Home', href: '/' },
    { label: 'Support & Help' }
  ],
  '/login': [
    { label: 'Home', href: '/' },
    { label: 'Login' }
  ],
  '/dashboard': [
    { label: 'Home', href: '/' },
    { label: 'Dashboard' }
  ],
  '/water-profile': [
    { label: 'Home', href: '/' },
    { label: 'Dashboard', href: '/dashboard' },
    { label: 'Water Quality Analysis' }
  ],
  '/formulation': [
    { label: 'Home', href: '/' },
    { label: 'Dashboard', href: '/dashboard' },
    { label: 'Crop Formulation' }
  ],
  '/salt-library': [
    { label: 'Home', href: '/' },
    { label: 'Dashboard', href: '/dashboard' },
    { label: 'Nutrient Salt Library' }
  ],
  '/stock-solutions': [
    { label: 'Home', href: '/' },
    { label: 'Dashboard', href: '/dashboard' },
    { label: 'Stock Solution Preparation' }
  ],
  '/calculation': [
    { label: 'Home', href: '/' },
    { label: 'Dashboard', href: '/dashboard' },
    { label: 'Unified Calculator' }
  ],
  '/results': [
    { label: 'Home', href: '/' },
    { label: 'Dashboard', href: '/dashboard' },
    { label: 'Calculator', href: '/calculation' },
    { label: 'Results' }
  ]
};

const BreadcrumbSEO: React.FC<BreadcrumbSEOProps> = ({ 
  customBreadcrumbs, 
  showHome = true 
}) => {
  const location = useLocation();
  
  // Get breadcrumbs for current route
  const breadcrumbs = customBreadcrumbs || BREADCRUMB_CONFIG[location.pathname] || [
    { label: 'Home', href: '/' },
    { label: 'Page' }
  ];

  // Filter out home if not needed
  const filteredBreadcrumbs = showHome ? breadcrumbs : breadcrumbs.slice(1);

  // Generate structured data for breadcrumbs
  const breadcrumbStructuredData = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumbs.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.label,
      "item": item.href ? `https://flahafast.flaha.org${item.href}` : undefined
    }))
  };

  // Don't show breadcrumbs on home page unless custom ones are provided
  if (location.pathname === '/' && !customBreadcrumbs) {
    return (
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(breadcrumbStructuredData, null, 0)
        }}
      />
    );
  }

  return (
    <>
      {/* Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(breadcrumbStructuredData, null, 0)
        }}
      />
      
      {/* Visual Breadcrumbs */}
      <Box sx={{ mb: 2 }}>
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          aria-label="breadcrumb navigation"
          sx={{
            '& .MuiBreadcrumbs-separator': {
              color: 'text.secondary'
            }
          }}
        >
          {filteredBreadcrumbs.map((item, index) => {
            const isLast = index === filteredBreadcrumbs.length - 1;
            
            if (isLast || !item.href) {
              return (
                <Typography
                  key={index}
                  color="text.primary"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    fontWeight: isLast ? 600 : 400,
                    fontSize: '0.875rem'
                  }}
                >
                  {item.icon && (
                    <Box sx={{ mr: 0.5, display: 'flex', alignItems: 'center' }}>
                      {item.icon}
                    </Box>
                  )}
                  {item.label}
                </Typography>
              );
            }

            return (
              <Link
                key={index}
                color="inherit"
                href={item.href}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  textDecoration: 'none',
                  fontSize: '0.875rem',
                  '&:hover': {
                    textDecoration: 'underline',
                    color: 'primary.main'
                  }
                }}
              >
                {item.icon && (
                  <Box sx={{ mr: 0.5, display: 'flex', alignItems: 'center' }}>
                    {item.icon}
                  </Box>
                )}
                {item.label}
              </Link>
            );
          })}
        </Breadcrumbs>
      </Box>
    </>
  );
};

export default BreadcrumbSEO;
